/** The live view's keyboard layer, as a pure function (UX §9).
 *
 * The teacher stands in front of thirty people; the keys are what they actually press, and the
 * cap for each one is printed on the button that already did that job. Because every refusal
 * has to say why in one sentence, and because "Space three times in two seconds must open one
 * question and close it once" is a rule about time rather than about React, the whole table
 * lives here as data: `resolve(key, ctx)` answers with an action, a refusal, or nothing.
 *
 * Nothing in this module touches the DOM. `useLiveShortcuts` supplies the context, applies the
 * scope rule (a single key fires only when focus is on the body or on the stage) and performs
 * the action.
 */

export type LiveState = "lobby" | "item_open" | "item_closed" | "intervention" | "ended";

export type LiveAction =
  /** the primary button's action, whatever it currently says */
  | { kind: "ask" }
  | { kind: "close" }
  | { kind: "aloud" }
  | { kind: "extend"; seconds: number }
  | { kind: "setTimer"; seconds: number }
  | { kind: "reveal"; mode: "correct" | "distribution" }
  | { kind: "revote" }
  | { kind: "skip" }
  | { kind: "move"; delta: 1 | -1 }
  | { kind: "tab"; to: "next" | "asked" }
  | { kind: "room" }
  | { kind: "quiet" }
  | { kind: "projector" }
  | { kind: "explain" }
  | { kind: "takeover" }
  | { kind: "help" }
  | { kind: "escape" };

/** An action to take, a reason to print, or nothing at all (the key is not ours). */
export type Resolution = { action: LiveAction } | { refuse: string } | null;

export type KeyContext = {
  /** The room is reachable: the cloud socket is live, or a local host is answering. */
  live: boolean;
  state: LiveState;
  /** Something is queued in Next (so the primary can ask it). */
  queued: boolean;
  /** Next has a selection to skip or ask. */
  selection: boolean;
  /** The closed round had at least one answer. */
  answered: boolean;
  /** The closed round has a correct answer — its own, or one the teacher gave afterwards. */
  keyed: boolean;
  /** An AI explanation exists for the closed round. */
  explanation: boolean;
  /** Another device owns the session. */
  remote: boolean;
  deckOpen: boolean;
  tab: "next" | "asked";
  /** A command sent to the engine has not been answered yet. */
  inFlight: boolean;
  now: number;
  /** When the primary last fired on this device (any source). */
  firedAt: number;
  /** When the current round closed on this device. */
  closedAt: number;
};

/** A bounced tap or a stuttered key must not open and close a question. */
export const BOUNCE_MS = 800;
/** After a close the space bar is inert for long enough to see the bars appear; the button
 * beside it is live at once, because a hand that moved to the button chose it. */
export const AFTER_CLOSE_MS = 1500;

const REFUSE = {
  notLive: "live.refuse.notLive",
  closeFirst: "live.refuse.closeFirst",
  needClosed: "live.refuse.needClosed",
  noKey: "live.refuse.noKey",
  noAnswers: "live.refuse.noAnswers",
  nothingToSkip: "live.refuse.nothingToSkip",
  noExplanation: "live.refuse.noExplanation",
  ended: "live.refuse.ended",
  revealMoved: "live.refuse.revealMoved",
  revoteOnlyLast: "live.done.revoteOnlyLast",
  handoff: "live.handoffBetweenItems",
} as const;

const open = (c: KeyContext) => c.state === "item_open";
const closed = (c: KeyContext) => c.state === "item_closed" || c.state === "intervention";

/** Keys that stay live when the session has ended: reading, never acting. The arrows are in
 * the set because the deck is the record of the lesson and reading it back is not a command —
 * and because the bar still prints ⌷↓ on the deck button after the end. */
const AFTER_END = new Set([
  "?",
  "Escape",
  "q",
  "Q",
  "g",
  "G",
  "p",
  "P",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
]);

export function resolve(key: string, c: KeyContext): Resolution {
  if (key === "Escape") return { action: { kind: "escape" } };
  if (key === "?") return { action: { kind: "help" } };
  if (c.state === "ended" && !AFTER_END.has(key)) {
    // Not silence: the end card's buttons are the only way on, and the line says so.
    return isOurs(key) ? { refuse: REFUSE.ended } : null;
  }
  // R showed the answer until 2026-09-11, when the owner moved it to A ("probably a
  // reasonable shortcut for show answer is A not R"). A hand that learned R here gets a
  // signpost rather than silence; the whole layer's rule is that a key never fails quietly.
  // Retire this line once no one is pressing R any more.
  if (key === "r" || key === "R") return { refuse: REFUSE.revealMoved };

  if (key === "ArrowDown" || key === "ArrowUp") {
    if (c.deckOpen && c.tab === "asked") return null;
    return c.queued ? { action: { kind: "move", delta: key === "ArrowDown" ? 1 : -1 } } : null;
  }
  if (key === "ArrowRight" || key === "ArrowLeft") {
    if (!c.deckOpen) return null;
    return { action: { kind: "tab", to: key === "ArrowRight" ? "asked" : "next" } };
  }

  switch (key.toLowerCase()) {
    case "g":
      return { action: { kind: "room" } };
    case "q":
      return { action: { kind: "quiet" } };
    case "p":
      return { action: { kind: "projector" } };
  }

  // Everything below reaches the room, so it needs the room.
  if (!c.live) return isOurs(key) ? { refuse: REFUSE.notLive } : null;

  if (key === " " || key === "Spacebar") return space(c);

  if (/^[1-9]$/.test(key)) {
    const seconds = Number(key) * 10; // owner decision: the digit is tens of seconds
    return { action: open(c) ? { kind: "extend", seconds } : { kind: "setTimer", seconds } };
  }

  switch (key.toLowerCase()) {
    case "a":
      // A for answer (owner, 2026-09-11). It sits on the home row beside D for the shares
      // and S for a question asked aloud, under the hand that is already resting there.
      if (open(c)) return { refuse: REFUSE.closeFirst };
      if (!closed(c)) return { refuse: REFUSE.needClosed };
      if (!c.answered) return { refuse: REFUSE.noAnswers };
      if (!c.keyed) return { refuse: REFUSE.noKey };
      return { action: { kind: "reveal", mode: "correct" } };
    case "d":
      if (open(c)) return { refuse: REFUSE.closeFirst };
      if (!closed(c)) return { refuse: REFUSE.needClosed };
      if (!c.answered) return { refuse: REFUSE.noAnswers };
      return { action: { kind: "reveal", mode: "distribution" } };
    case "v":
      if (open(c)) return { refuse: REFUSE.closeFirst };
      if (!closed(c)) return { refuse: REFUSE.needClosed };
      if (c.deckOpen && c.tab === "asked") return { refuse: REFUSE.revoteOnlyLast };
      if (!c.answered) return { refuse: REFUSE.noAnswers };
      return { action: { kind: "revote" } };
    case "n":
      if (c.deckOpen && c.tab === "asked") return { refuse: REFUSE.nothingToSkip };
      if (!c.selection) return { refuse: REFUSE.nothingToSkip };
      return { action: { kind: "skip" } };
    case "s":
      // S for "say it out loud" — Спросить in Russian. A is the answer now.
      return { action: { kind: "aloud" } };
    case "e":
      if (!closed(c)) return { refuse: REFUSE.needClosed };
      if (!c.explanation) return { refuse: REFUSE.noExplanation };
      return { action: { kind: "explain" } };
    case "t":
      if (!c.remote) return null;
      if (open(c)) return { refuse: REFUSE.handoff };
      return { action: { kind: "takeover" } };
  }
  return null;
}

/** Space is defined as "the primary button's action", never as a state machine of its own
 * (§9.3). Whatever the big button says, Space does — plus the guards that keep a resting
 * thumb from asking a question in front of a class. */
function space(c: KeyContext): Resolution {
  if (c.inFlight) return null;
  if (c.now - c.firedAt < BOUNCE_MS) return null;
  // Space must never re-ask from history; going back to Next is the safe reading here.
  if (c.deckOpen && c.tab === "asked") return { action: { kind: "tab", to: "next" } };
  if (open(c)) return { action: { kind: "close" } };
  if (closed(c) && c.now - c.closedAt < AFTER_CLOSE_MS) return null;
  // Firing a shape-only question on a stray key press is the one thing we must not do.
  if (!c.queued) return { action: { kind: "aloud" } };
  return { action: { kind: "ask" } };
}

/** Is this a key the live view claims? Used only to decide whether a refusal is owed. `r` is
 * in the set for the sake of the ended state alone: everywhere else it is answered above. */
function isOurs(key: string): boolean {
  return key === " " || key === "Spacebar" || /^[1-9]$/.test(key) || "advnsetr".includes(key.toLowerCase());
}

/** The caps printed on the buttons, and the list in the shortcut sheet. `keys` are rendered
 * in `Keycap`; `label` and `when` are i18n keys. */
export type ShortcutDoc = { keys: string[]; label: string; group: "open" | "closed" | "queue" | "always" };

export const SHORTCUTS: ShortcutDoc[] = [
  { keys: ["Space"], label: "live.keys.space", group: "always" },
  { keys: ["1–9"], label: "live.keys.digits", group: "open" },
  { keys: ["A"], label: "live.keys.reveal", group: "closed" },
  { keys: ["D"], label: "live.keys.shares", group: "closed" },
  { keys: ["V"], label: "live.keys.revote", group: "closed" },
  { keys: ["E"], label: "live.keys.explain", group: "closed" },
  { keys: ["↑", "↓"], label: "live.keys.move", group: "queue" },
  { keys: ["←", "→"], label: "live.keys.tabs", group: "queue" },
  { keys: ["N"], label: "live.keys.skip", group: "queue" },
  { keys: ["S"], label: "live.keys.aloud", group: "always" },
  { keys: ["G"], label: "live.keys.room", group: "always" },
  { keys: ["Q"], label: "live.keys.quiet", group: "always" },
  { keys: ["P"], label: "live.keys.projector", group: "always" },
  { keys: ["T"], label: "live.keys.takeover", group: "always" },
  { keys: ["?"], label: "live.keys.help", group: "always" },
  { keys: ["Esc"], label: "live.keys.escape", group: "always" },
];
